import React from 'react';
import { styled } from '../../stitches.config';
import { IPodcast } from '../types/Podcast';
import Heading from '../atoms/Heading';
import { bodyLarge } from '../commonStyles/Fonts';

import { ReactComponent as PlayIcon } from "../../assets/img/podcastPlayer/podcast_play.svg?react";
import { ReactComponent as PauseIcon } from "../../assets/img/podcastPlayer/podcast_pause.svg?react";
import Media from '../../components/common/Media';
import Logo from '../atoms/Logo';
import { formatTime, usePlayerState, usePodcastPlayer } from '../../context/PodcastPlayerContext';
import Link from '../../components/Link';
import useMediaQuery from '../../helpers/useMediaQuery';
import PodcastPosterPlaceholder from './PodcastPlayer/PodcastPosterPlaceholder';

interface IPodcastList {
    title: string;
    leadOnMobile?: boolean;
    items: IPodcast[];
};

const PodcastList: React.FC<IPodcastList> = ({ title, items, leadOnMobile = false }) => {

    return (
        <ListBox>
            <Heading title={title} specialHeading={true} />
            {items.map((item, idx) => (
                <PodcastListItem
                    {...item}
                    key={idx}
                    leadOnMobile={leadOnMobile}
                />
            ))}
        </ListBox>
    );

};

const ListBox = styled('div', {
    display: 'flex',
    flexFlow: 'column',
    margin: '4rem 0',
});

/*List item*/
export default PodcastList;

interface IPodcastListItem extends IPodcast {
    leadOnMobile?: boolean;
};

export const PodcastListItem: React.FC<IPodcastListItem> = ({ OID, title, seriesURL, seriesName, author, lead, url, date, poster, videoId, duration, leadOnMobile = false }) => {

    const shortLead = lead.slice(0, 180);

    const isMobile = useMediaQuery('mobile');

    const podcastContext = usePodcastPlayer();
    const { isVideoLoading, pause, setEpisode } = podcastContext;
    const isPlay = usePlayerState<boolean>('isPlay', podcastContext, videoId);

    const playEpisode = () => {
        if (!videoId)
            return;

        if (isVideoLoading)
            return;

        if (isPlay)
            return pause()

        setEpisode({
            episode_id: OID,
            video_id: parseInt(videoId)
        });
    };

    const playButton = !isPlay ? <PlayIcon width="12px" height="12px" /> : <PauseIcon width="12px" height="12px" />;

    return (
        <ListItemBox>
            <PodcastPoster smallMobile={!leadOnMobile}>
                {poster && <Media
                    media={poster.media}
                    src={poster.img}
                    alt={title}
                    height={350}
                    width={350}
                    aspectRatio={1}
                    preserveRatio={true}
                />}
                {!poster && <PodcastPosterPlaceholder />}
            </PodcastPoster>
            <PodcastTitle smallMobile={!leadOnMobile}>
                {leadOnMobile && seriesName && seriesURL &&
                    (<Link to={`${seriesURL}`}>
                        <span className="series">{seriesName}: </span>
                    </Link>)}
                <Link to={`/${OID}`}>
                    {title}
                </Link>
            </PodcastTitle>
            <PodcastLead showMobile={leadOnMobile}
                className={bodyLarge()}
            >
                {shortLead + (lead.length > shortLead.length ? '...' : '')}
            </PodcastLead>
            <PodcastPlayer className={bodyLarge()} rightMobile={!leadOnMobile}>
                {isMobile ? (
                    <Link to={`/${OID}`}>
                        {playButton}
                    </Link>
                ) : (
                    <button onClick={playEpisode}>
                        {playButton}
                    </button>
                )}
                {duration && <p className="duration">{formatTime(duration)}</p>}
                <p>{date}</p>
            </PodcastPlayer>
        </ListItemBox>
    );

};

const PodcastTitle = styled('p', {
    margin: '0.25em 0',
    fontFamily: "$grotesk",
    letterSpacing: "0.2px",
    fontWeight: "700",
    color: '$grayscale100',
    '.series': {
        color: '$primary'
    },
    'a,a:hover,a:active': {
        color: '$grayscale100',
        textDecoration: 'none'
    },
    variants: {
        smallMobile: {
            true: {
                fontSize: 18,
                lineHeight: "24px",
                '@bp3': {
                    fontSize: 24,
                    lineHeight: "30px",
                },
            },
            false: {
                fontSize: 24,
                lineHeight: "30px",
            }
        }
    }
});

const PodcastPoster = styled('div', {
    overflow: 'hidden',
    position: 'relative',
    'img': {
        position: 'absolute',
        width: '100%',
        height: '100%',
        objectPosition: 'center',
        objectFit: 'cover'
    },
    variants: {
        smallMobile: {
            false: {
                marginBottom: '2.4rem',
                marginRight: '2.4rem',
                '@bp3': {
                    float: 'left',
                    paddingBottom: '0',
                    width: '200px',
                    height: '200px',
                },
            },
            true: {
                float: 'left',
                width: "88px",
                height: "88px",
                marginRight: "1.6rem",
                '@bp3': {
                    width: '200px',
                    height: '200px',
                },
            }
        }
    }
});

const PodcastPlayer = styled('div', {
    color: '$grayscale100',
    marginTop: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    '& p': {
        margin: 0
    },
    '& a, & button': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: "0 16px 0 0",
        width: '32px',
        height: '32px',
        borderRadius: '32px',
        borderWidth: 0,
        backgroundColor: '$primary',
        "svg": {
            marginLeft: '2px',
            fill: '#fff'
        }
    },
    '& .duration::after': {
        content: '',
        display: 'inline-block',
        height: '12px',
        width: '1px',
        background: '$grayscale100',
        margin: '0 0.8rem',
        opacity: '0.1'
    },
    variants: {
        rightMobile: {
            true: {
                paddingTop: '1rem',
                width: '100%',
                justifyContent: 'flex-start',
                '@bp3': {
                    paddingTop: '0rem',
                    width: 'auto',
                    justifyContent: 'flex-start'
                },
            }
        }
    }
});

const PodcastLead = styled('div', {
    margin: '0.5em 0',
    color: '$grayscale100',
    variants: {
        showMobile: {
            false: {
                display: 'none',
                "@bp3": {
                    display: "block",
                }
            }
        }
    }
});

const ListItemBox = styled('div', {
    marginTop: '4rem',
    paddingBottom: '2.4rem',
    '@bp3': {
        paddingBottom: '4rem',
    },
    borderBottom: '1px solid $grayscale25',
    '&:nth-child(2)': {
        marginTop: '0'
    },
});